import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { getPointsBalance } from '../../store/slices/pointsSlice';
import { getAuraScore } from '../../store/slices/auraSlice';
import styled from 'styled-components';
import { formatAuraScoreDisplay } from '../../utils/formatAuraScore';
import { formatWalletBalanceEgp } from '../../utils/formatPrice';
import WalletIcon from '../icons/WalletIcon';

const STORAGE_KEY = 'flea_wallet_aura_badge_pos_v1';
const DRAG_THRESHOLD_PX = 6;
const BADGE_W = 88;
const BADGE_H = 52;

function loadSavedPosition() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (typeof parsed?.x === 'number' && typeof parsed?.y === 'number') {
      return { x: parsed.x, y: parsed.y };
    }
  } catch {
    // ignore
  }
  return null;
}

function clampPosition(x, y) {
  const maxX = Math.max(8, window.innerWidth - BADGE_W - 8);
  const maxY = Math.max(8, window.innerHeight - BADGE_H - 8);
  return {
    x: Math.min(maxX, Math.max(8, x)),
    y: Math.min(maxY, Math.max(8, y)),
  };
}

function defaultPosition() {
  return clampPosition(window.innerWidth - BADGE_W - 16, 72);
}

const FloatingBadge = styled.button`
  position: fixed;
  z-index: 12000;
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  padding: 0.35rem 0.6rem;
  min-width: 4.75rem;
  background: linear-gradient(135deg, #0f766e 0%, #14b8a6 55%, #0d9488 100%);
  border-radius: 8px;
  border: none;
  color: #fff;
  font-size: 0.72rem;
  font-weight: 600;
  font-family: inherit;
  box-shadow: 0 4px 14px rgba(15, 118, 110, 0.35);
  cursor: grab;
  touch-action: none;
  user-select: none;
  -webkit-user-select: none;
  transition: box-shadow 0.15s ease;

  &:active {
    cursor: grabbing;
  }

  &:hover {
    box-shadow: 0 6px 16px rgba(15, 118, 110, 0.42);
  }

  @media (max-width: 768px) {
    padding: 0.3rem 0.5rem;
    font-size: 0.68rem;
  }
`;

const BadgeRow = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  white-space: nowrap;
  pointer-events: none;
`;

const PointsAuraCorner = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isAuthenticated } = useSelector((state) => state.auth);
  const balance = useSelector((state) => state.points?.balance);
  const { score: auraScore } = useSelector((state) => state.aura);
  const pointsLoading = useSelector((state) => state.points?.loading);
  const auraLoading = useSelector((state) => state.aura?.loading);

  const [pos, setPos] = useState(() => {
    if (typeof window === 'undefined') return { x: 16, y: 72 };
    return loadSavedPosition() || defaultPosition();
  });

  const dragRef = useRef({
    active: false,
    moved: false,
    startX: 0,
    startY: 0,
    originX: 0,
    originY: 0,
    pointerId: null,
  });

  useEffect(() => {
    if (isAuthenticated) {
      dispatch(getPointsBalance());
      dispatch(getAuraScore());
    }
  }, [dispatch, isAuthenticated]);

  useEffect(() => {
    const onResize = () => {
      setPos((prev) => clampPosition(prev.x, prev.y));
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const persist = useCallback((next) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch {
      // ignore
    }
  }, []);

  const onPointerDown = (e) => {
    if (e.button != null && e.button !== 0) return;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    dragRef.current = {
      active: true,
      moved: false,
      startX: e.clientX,
      startY: e.clientY,
      originX: pos.x,
      originY: pos.y,
      pointerId: e.pointerId,
    };
  };

  const onPointerMove = (e) => {
    const drag = dragRef.current;
    if (!drag.active) return;
    const dx = e.clientX - drag.startX;
    const dy = e.clientY - drag.startY;
    if (!drag.moved && (Math.abs(dx) > DRAG_THRESHOLD_PX || Math.abs(dy) > DRAG_THRESHOLD_PX)) {
      drag.moved = true;
    }
    if (!drag.moved) return;
    e.preventDefault();
    setPos(clampPosition(drag.originX + dx, drag.originY + dy));
  };

  const endDrag = (e) => {
    const drag = dragRef.current;
    if (!drag.active) return;
    drag.active = false;
    try {
      e.currentTarget.releasePointerCapture?.(drag.pointerId);
    } catch {
      // ignore
    }
    if (drag.moved) {
      setPos((prev) => {
        const next = clampPosition(prev.x, prev.y);
        persist(next);
        return next;
      });
      return;
    }
    navigate('/wallet-balance-and-aura');
  };

  if (!isAuthenticated) return null;

  const pointsDisplay = pointsLoading ? '…' : (balance != null ? formatWalletBalanceEgp(balance) : '—');
  const auraDisplay = auraLoading ? '…' : formatAuraScoreDisplay(auraScore);

  return (
    <FloatingBadge
      type="button"
      title="Drag to move · Tap for Aura & Wallet"
      aria-label="Aura and Wallet. Drag to reposition."
      style={{ left: pos.x, top: pos.y }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
    >
      <BadgeRow>
        <WalletIcon size={13} color="#fff" />
        {pointsDisplay}
      </BadgeRow>
      <BadgeRow>⭐ {auraDisplay}</BadgeRow>
    </FloatingBadge>
  );
};

export default PointsAuraCorner;
